import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Divider,
  useMediaQuery
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatDate';
import api from '../../services/api';
import ServerDataTable from '../ServerDataTable/ServerDataTable';
import StatusBadge from '../StatusBadge';
import JsonDataDisplay from '../common/JsonDataDisplay';

export default function SyncedProductsTab() {

  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // State for search and pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    is_deleted: '',
    last_action: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    is_deleted: '',
    last_action: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  // Function to update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Clear existing filter parameters
    Object.keys(filters).forEach(key => {
      params.delete(key);
    });

    // Add non-empty filter values to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());

    // Handle search term - add if not empty, remove if empty
    params.set('search', currentSearchTerm || '');

    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters
    const filtersFromURL = {
      is_deleted: params.get('is_deleted') || '',
      last_action: params.get('last_action') || '',
      updated_at_start: params.get('updated_at_start') || '',
      updated_at_end: params.get('updated_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Sync modal filters with applied filters when dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
      setPageIndex(0); // Reset to first pageIndex when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch product mappings with updated pagination and filters
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['productMappings', pageIndex, pageSize, debouncedSearchTerm, appliedFilters],
    queryFn: async () => {
      const params = {
        offset: pageIndex * pageSize,
        limit: pageSize,
        search: debouncedSearchTerm || undefined,
        is_deleted: appliedFilters.is_deleted || undefined,
        last_action: appliedFilters.last_action || undefined,
        updated_at_start: appliedFilters.updated_at_start || undefined,
        updated_at_end: appliedFilters.updated_at_end || undefined
      };

      const response = await api.get('/product-sync/product-mappings', {
        params
      });

      // If we're not on the first pageIndex and the total results would make current pageIndex empty
      const totalPages = Math.ceil(
        response.data.data.pagination?.total / pageSize
      );
      if (pageIndex > 0 && pageIndex >= totalPages) {
        setPageIndex(0);
        // Re-fetch with new pageIndex
        return refetch();
      }

      return {
        mappings: response.data.data.items || [],
        pagination: response.data.data.pagination || {
          total: 0,
          limit: pageSize,
          offset: 0,
          hasMore: false
        }
      };
    },
    staleTime: 1000 * 60 * 2 // 2 minutes
  });

  const handleRefresh = () => {
    refetch();
  };

  const handleExportToCSV = async () => {
    try {
      setIsExporting(true);

      // Build query parameters with current filters
      const params = new URLSearchParams();

      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      if (appliedFilters.is_deleted !== '') {
        params.append('is_deleted', appliedFilters.is_deleted);
      }
      if (appliedFilters.last_action) {
        params.append('last_action', appliedFilters.last_action);
      }
      if (appliedFilters.updated_at_start) {
        params.append('updated_at_start', appliedFilters.updated_at_start);
      }
      if (appliedFilters.updated_at_end) {
        params.append('updated_at_end', appliedFilters.updated_at_end);
      }

      // Make API call to export endpoint
      const response = await api.get('/product-sync/product-mappings/export', {
        params,
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'products_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting products to CSV:', error);
      // You might want to show a toast/notification here
      alert('Failed to export products. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, newSearchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  const handleOpenViewDialog = (data) => {
    setViewData(data);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Clear filters and close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      is_deleted: '',
      last_action: '',
      updated_at_start: '',
      updated_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  // Format Shopify URL for display
  const formatShopifyId = (shopifyId) => {
    if (!shopifyId) return 'N/A';
    return shopifyId;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: true,
      align: 'center'
    },
    {
      field: 'erp_item_code',
      headerName: 'ERP Item Code',
      width: 200,
      sortable: true
    },
    {
      field: 'shopify_product_id',
      headerName: 'Shopify Product ID',
      width: 350,
      sortable: true,
      renderCell: ({ value }) => formatShopifyId(value)
    },
    {
      field: 'is_deleted',
      headerName: 'Is Deleted',
      width: 150,
      sortable: true,
      align: 'left',
      renderCell: ({ value }) => (
        value === 1 ? (
          <StatusBadge customLabel='Yes' customColor='error' customize={true} />
        ) : value === 0 ? (
          <StatusBadge customLabel='No' customColor='success' customize={true} />
        ) : (
          <StatusBadge status='N/A' />
        )
      )
    },
    {
      field: 'last_action',
      headerName: 'Last Action',
      width: 150,
      sortable: true,
      align: 'left',
      renderCell: ({ value }) => (
        <StatusBadge status={value} />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: '',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        return (
          <Tooltip title='View Details'>
            <IconButton size='small' onClick={() => handleOpenViewDialog(row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  return (
    <Box>
      <ServerDataTable
        data={data?.mappings || []}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={data?.pagination?.total || 0}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isFetching}
        isError={error}
        error={error}
        noDataMessage='No synced products found. Try a different search term or sync products from the Sync Operations tab.'
        totalPages={Math.ceil(data?.pagination?.total / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by ERP Item Code, Shopify Product ID'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some(value => value !== '') && (
                <Chip
                  size='small'
                  label={isFetching ? '' : Object.values(appliedFilters).filter(value => value !== '').length}
                  color='secondary'
                  sx={{ ml: 1 }}
                  disabled={isFetching}
                />
              )}
            </Button>
            <Button
              variant='contained'
              color='primary'
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isFetching}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
            >
              Refresh
            </Button>
            <Button
              variant='contained'
              color='primary'
              startIcon={<GetAppIcon />}
              onClick={handleExportToCSV}
              disabled={isFetching || isExporting}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </Box>
        }
      />

      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Products
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Is Deleted</InputLabel>
              <Select
                name='is_deleted'
                value={filterForm.is_deleted}
                onChange={handleFilterFormChange}
                label='Is Deleted'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='1'>Yes</MenuItem>
                <MenuItem value='0'>No</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Last Action</InputLabel>
              <Select
                name='last_action'
                value={filterForm.last_action}
                onChange={handleFilterFormChange}
                label='Last Action'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='create'>Create</MenuItem>
                <MenuItem value='update'>Update</MenuItem>
              </Select>
            </FormControl>

            <Divider>Updated At Range</Divider>

            <TextField
              name='updated_at_start'
              label='From Date'
              type='date'
              value={filterForm.updated_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              name='updated_at_end'
              label='To Date'
              type='date'
              value={filterForm.updated_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}
        >
          Product Details
          <Box>
            <Tooltip
              title={isDialogMaximized ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
                {isDialogMaximized ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  ERP Item Code
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.erp_item_code}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div' sx={{ wordBreak: 'break-all !important' }}>
                  Shopify Product ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant='body1' gutterBottom sx={{ mr: 1 }} color='text.secondary' component='div'>
                    {viewData.shopify_product_id || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Last Action
                </Typography>
                <Box component='div' sx={{ mt: 1 }}>
                  <StatusBadge status={viewData.last_action} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Created
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Last Updated
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.updated_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Is Deleted
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.is_deleted ? <StatusBadge customLabel='Yes' customColor='error' customize={true} /> : <StatusBadge customLabel='No' customColor='success' customize={true} />}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={viewData.product_data} title='Product Data' />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
